import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { FileText, Upload, Loader2, CheckCircle2 } from 'lucide-react';

const STATUS_BADGE = {
  submitted_by_vendor: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700',
  matched: 'bg-emerald-50 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-800',
  paid: 'bg-green-100 text-green-800',
  rejected: 'bg-red-50 text-red-600',
};

export default function VendorInvoices() {
  const { vendorFetch } = useVendorAuth();
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ po_number: '', invoice_no: '', invoice_date: '', amount: '', gst_amount: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => vendorFetch('/partner/invoices').then(d => setInvoices(d.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.invoice_no) { toast.error('Invoice number is required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append('file', file);
      const token = localStorage.getItem('vendor_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/invoices`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      toast.success('Invoice submitted for 3-way matching');
      setShowForm(false);
      setForm({ po_number: '', invoice_no: '', invoice_date: '', amount: '', gst_amount: '' });
      setFile(null);
      load();
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    setUploading(false);
  };

  return (
    <div className="space-y-5" data-testid="vendor-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-xl text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500 mt-0.5">Upload invoices — they enter the 3-way match queue on Ellora EPC's side.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold hover:bg-[#2e3a5c]"
          data-testid="upload-invoice-btn">
          <Upload size={13} /> Upload Invoice
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="invoice-upload-form">
          <div className="font-bold text-sm text-slate-900">New Invoice</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">PO Number</label>
              <input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })}
                placeholder="PO-202606-XXXX" className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Invoice Number *</label>
              <input value={form.invoice_no} onChange={e => setForm({ ...form, invoice_no: e.target.value })}
                placeholder="INV-001" className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" required data-testid="invoice-no-input" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Amount (₹)</label>
              <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00" className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">GST Amount (₹)</label>
              <input type="number" value={form.gst_amount} onChange={e => setForm({ ...form, gst_amount: e.target.value })}
                placeholder="0.00" className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" /></div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Invoice PDF</label>
              <label className={`flex items-center gap-2 border-2 border-dashed rounded px-3 py-2.5 cursor-pointer text-xs text-slate-500 hover:border-[#3b4872] ${file ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300'}`}>
                {file ? <><CheckCircle2 size={13} />{file.name}</> : <><Upload size={13} /> Upload PDF</>}
                <input type="file" accept=".pdf,.jpg,.png" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={uploading}
              className="px-5 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold disabled:opacity-50">
              {uploading ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}{uploading ? 'Uploading…' : 'Submit Invoice'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-sm text-xs font-bold text-slate-600">Cancel</button>
          </div>
        </form>
      )}

      {invoices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <FileText size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-semibold">No invoices yet</div>
          <div className="text-xs mt-1">Upload your first invoice to start tracking payments.</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full dense-table text-xs">
            <thead><tr><th>Invoice No</th><th>PO Number</th><th>Date</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`}>
                  <td className="font-mono">{inv.invoice_no}</td>
                  <td className="text-slate-500">{inv.po_number || '—'}</td>
                  <td>{inv.invoice_date || '—'}</td>
                  <td className="text-right tabular-nums">₹{(inv.gross_amount || 0).toLocaleString('en-IN')}</td>
                  <td><span className={`px-2 py-0.5 text-[10px] font-bold rounded-sm ${STATUS_BADGE[inv.status] || 'bg-slate-100 text-slate-600'}`}>{inv.status?.replace(/_/g, ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
